import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Input,
  message,
  Button,
  Space,
  Spin,
  Typography,
  notification,
  Modal,
  Card,
  Tooltip,
  Alert,
  Select,
  Row,
  Col,
  Statistic,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  EditOutlined,
  ClearOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import getVisitasTecnicas from "../../../services/visitas_tecnicas/visita_tecnica/getVisitasTecnicasService";
import FormularioVisitaTecnica from "./FormVisitaTecnica";
import PdfVisitaTecnica from "./pdfVisitaTecnica";
import getVisitaTecnicaById from "../../../services/visitas_tecnicas/visita_tecnica/getVisitaTecnicaByIdService";
import { selectAppPermissions } from "../../../store/auth/selectors";
import ExportVisitasTecnicas from "./VisitasTecnicasExcel";
import { useSelector } from "react-redux";
import config from "../../../config";
import deleteVisitaTecnica from "../../../services/visitas_tecnicas/visita_tecnica/deleteVisitaTecnicaService";

const baseUrl = config.baseUrl;

const { Text, Title } = Typography;
const { Option } = Select;

const TablaVisitaTecnica = () => {
  const [visitasTecnicas, setVisitasTecnicas] = useState([]);
  const [selectedTipoLineaProductiva, setSelectedTipoLineaProductiva] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const [showForm, setShowForm] = useState(false); // Estado para controlar la visibilidad del formulario
  const [showFormModal, setShowFormModal] = useState(false); // Estado para controlar la visibilidad del modal

  const [initialData, setInitialData] = useState({}); // Estado para manejar los datos iniciales

  const [showPdfVisitasTecnicas, setShowPdfVisitasTecnicas] = useState(false); // Estado para mostrar el componente pdfVisitasTecnicas

  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);

  const appName = "visitas";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );
  const permissionInsert = permissions.priv_insert === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionPrint = permissions.priv_print === "Y";
  const permissionDelete = permissions.priv_delete === "Y";

  const usernameFromState = useSelector((state) => state.auth.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const handleCancel = () => {
    if (initialData && Object.keys(initialData).length > 0) {
      Modal.confirm({
        title: "¿Está segur@ de que desea cerrar?",
        content:
          "Si cierra esta ventana, la información ingresada no se guardará.",
        okText: "Sí, cerrar",
        cancelText: "No",
        onOk: () => {
          setShowFormModal(false);
          setInitialData({});
        },
      });
    } else {
      Modal.confirm({
        title: "¿Está segur@ de que desea cerrar?",
        content:
          "Si cierra esta ventana, la información ingresada no se guardará.",
        okText: "Sí, cerrar",
        cancelText: "No",
        onOk: () => {
          setShowFormModal(false);
          setInitialData({});
        },
      });
    }
  };
  const fetchVisitasTecnicas = async () => {
    try {
      const response = await getVisitasTecnicas(usernameFromState.email);
      const { status, data } = response;

      if (status === 200) {
        setVisitasTecnicas(data);
      } else {
        notification.error({
          message: "Error",
          description: message || "Failed to fetch visitas técnicas data.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    filterData(
      selectedDepartamento,
      selectedMunicipio,
      selectedTipoLineaProductiva,
      searchText
    );
  }, [
    visitasTecnicas,
    selectedDepartamento,
    selectedMunicipio,
    selectedTipoLineaProductiva,
    searchText,
  ]);
  const filterData = (departamento, municipio, tipo_lineaproductiva, texto) => {
    let filtered = [...visitasTecnicas];
    // Filtro por departamento (si está seleccionado en el select)
    if (departamento) {
      filtered = filtered.filter((v) => v.departamento === departamento);
    }
    // Filtro por municipio (si está seleccionado en el select)
    if (municipio) {
      filtered = filtered.filter((v) => v.municipio === municipio);
    }
    // Filtro por tipo de novedad (si está seleccionado en el select)
    if (tipo_lineaproductiva) {
      filtered = filtered.filter(
        (v) => v.linea_productiva === tipo_lineaproductiva
      );
    }

    if (texto?.trim()) {
      // Verifica que no sea nulo, vacío o solo espacios
      filtered = filtered.filter((visitaTecnica) =>
        Object.values(visitaTecnica).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(texto.toLowerCase())
        )
      );
    }

    // Actualiza los datos filtrados
    setFilteredData(filtered);
  };

  const fetchVisitaTecnicaById_update = async (record) => {
    try {
      const response = await getVisitaTecnicaById(record.id);
      const { status, data } = response;

      if (status) {
        const { detalle, ...restoData } = data;

        const detalleCompleto = {
          ...detalle,
          ...restoData,
        };

        setInitialData(detalleCompleto);
        setShowFormModal(true); // Abre el modal
      } else {
        notification.error({
          message: response.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Ocurrió un error al obtener la información de la visita.",
      });
    }
  };

  const fetchVisitaTecnicaById_pdf = async (record) => {
    try {
      if (record?.ruta_pdf != null) {
        // 👉 Generar URL con timestamp para evitar caché
        const urlConTimestamp = `${baseUrl}${
          record.ruta_pdf
        }?t=${new Date().getTime()}`;

        // Abrir el PDF en una nueva ventana
        window.open(urlConTimestamp, "_blank");
      } else {
        const response = await getVisitaTecnicaById(record.id);
        const { status, data } = response;

        if (status) {
          const { detalle, ...restoData } = data;

          const detalleCompleto = {
            ...detalle,
            ...restoData,
          };
          
          setInitialData(detalleCompleto);
          setShowPdfVisitasTecnicas(true); // Abre el modal
        } else {
          notification.error({
            message: response.message,
          });
        }
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    }
  };

  // 🗑️ eliminar
  const eliminarVisita = async (oid) => {
    try {
      const resp = await deleteVisitaTecnica(oid);
      if (resp.success) {
        message.success(resp.message);
        fetchVisitasTecnicas();
      } else {
        message.error("No se pudo eliminar la visita técnica.");
      }
    } catch (error) {
      message.error("No se pudo eliminar la visita técnica.");
    }
  };

  useEffect(() => {
    fetchVisitasTecnicas();
  }, []);

  useEffect(() => {
    // Obtener lista única de departamentos de los participants
    const uniqueDepartamentos = [
      ...new Set(visitasTecnicas.map((p) => p.departamento)),
    ].sort();
    setDepartamentos(uniqueDepartamentos);
    setSelectedDepartamento(null);
  }, [visitasTecnicas]);

  const handletTipoLineaProductiva = (value) => {
    setSelectedTipoLineaProductiva(value);
    filterData(selectedDepartamento, selectedMunicipio, value, searchText);
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    filterData(
      selectedDepartamento,
      selectedMunicipio,
      selectedTipoLineaProductiva,
      value
    );
  };

  const handleClearFilters = () => {
    setSelectedTipoLineaProductiva(null);
    setSelectedDepartamento(null);
    setSelectedMunicipio(null);
    setSearchText("");
    setFilteredData(visitasTecnicas);
  };

  const handleDepartamentoChange = (value) => {
    setSelectedDepartamento(value);
    setSelectedMunicipio(null); // Resetear municipio cuando cambia departamento

    // Obtener municipios del departamento seleccionado
    const municipiosFiltrados = [
      ...new Set(
        visitasTecnicas
          .filter((p) => p.departamento === value)
          .map((p) => p.municipio)
      ),
    ].sort();
    setMunicipios(municipiosFiltrados);

    // Filtrar datos
    filterData(value, null, selectedTipoLineaProductiva, searchText);
  };

  const handleMunicipioChange = (value) => {
    setSelectedMunicipio(value);
    filterData(
      selectedDepartamento,
      value,
      selectedTipoLineaProductiva,
      searchText
    );
  };

  const columns = [
    {
      title: "Acciones",
      key: "acciones",
      ellipsis: true,
      fixed: "left",
      width: 100,
      render: (text, record) => (
        <Space>
          {permissionUpdate && (
            <Tooltip title="Editar Visita Técnica">
              <Button
                style={{ backgroundColor: "#FFA500", color: "#fff" }}
                icon={<EditOutlined />}
                onClick={() => {
                  fetchVisitaTecnicaById_update(record);
                }}
              ></Button>
            </Tooltip>
          )}
          {permissionPrint && (
            <Tooltip title="Descargar PDF">
              <Button
                type="default"
                icon={<FilePdfOutlined />}
                style={{ backgroundColor: "#1890ff", color: "#fff" }}
                onClick={() => {
                  fetchVisitaTecnicaById_pdf(record);
                }}
              ></Button>
            </Tooltip>
          )}
          {permissionDelete && (
            <Tooltip title="Eliminar visita">
              <Popconfirm
                title="¿Desea eliminar esta visita?"
                description="Esta acción no se puede deshacer."
                okText="Sí, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => eliminarVisita(record.id)} // reemplaza con tu función real
              >
                <Button
                  type="default"
                  icon={<DeleteOutlined />}
                  style={{ backgroundColor: "#ff4d4f", color: "#fff" }}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Fecha registro",
      dataIndex: "fecha_registro",
      key: "fecha_registro",
      width: 130,
    },
    {
      title: "Número de visita",
      dataIndex: "numero_visita",
      key: "numero_visita",
      width: 120,
    },
    {
      title: "Código SIR",
      dataIndex: "codigoSIRR",
      key: "codigoSIRR",
      width: 120,
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 150,
    },
    {
      title: "Municipio",
      dataIndex: "municipio",
      key: "municipio",
      width: 150,
    },

    {
      title: "Nombre participante",
      dataIndex: "nombreParticipante",
      key: "nombreParticipante",
      width: 200,
    },

    {
      title: "Linea productiva",
      dataIndex: "linea_productiva",
      key: "linea_productiva",
      width: 200,
    },
    {
      title: "Codigo PRE",
      dataIndex: "codigoPREPP",
      key: "codigoPREPP",
      width: 150,
    },
    {
      title: "Nombre proyecto",
      dataIndex: "nombreProyecto",
      key: "nombreProyecto",
      width: 250,
    },
    {
      title: "Grupo territorial",
      dataIndex: "grupoTerritorial",
      key: "grupoTerritorial",
      width: 250,
    },
  ];

  return (
    <>
      {/* Renderiza solo el formulario si showForm es true */}
      {showForm ? (
        <div className="form-container">
          <FormularioVisitaTecnica initialData={{}} />{" "}
          {/* Pasa un objeto vacío para la creación */}
        </div>
      ) : showPdfVisitasTecnicas ? (
        <PdfVisitaTecnica
          formData={initialData}
          onClose={() => setShowPdfVisitasTecnicas(false)} // Función para cerrar el componente
        />
      ) : (
        <>
          {/* Aquí va el resto del contenido que deseas mostrar cuando no se está creando una novedad */}
          {loading ? (
            <div className="text-center">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Row gutter={[16, 16]} className="mb-4">
                <Col span={24}>
                  <div className="filter-container">
                    <Row gutter={[16, 16]} align="middle">
                      {/* Filtro Departamento */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Departamento
                          </Title>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Seleccione departamento"
                            allowClear
                            onChange={handleDepartamentoChange}
                            value={selectedDepartamento}
                          >
                            {departamentos.map((depto) => (
                              <Select.Option key={depto} value={depto}>
                                {depto}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </Col>

                      {/* Filtro Municipio */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Municipio
                          </Title>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Seleccione municipio"
                            allowClear
                            disabled={!selectedDepartamento}
                            onChange={handleMunicipioChange}
                            value={selectedMunicipio}
                          >
                            {municipios.map((muni) => (
                              <Select.Option key={muni} value={muni}>
                                {muni}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </Col>
                      {/* Filtro linea productiva */}
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Linea productiva
                          </Title>
                          <Select
                            value={selectedTipoLineaProductiva}
                            showSearch
                            onChange={handletTipoLineaProductiva}
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            placeholder="Seleccione la linea productiva"
                          >
                            <Option value="Agrícola">Agrícola</Option>
                            <Option value="Pecuaria">Pecuaria</Option>
                            <Option value="Servicios">Servicios</Option>
                            {/* ... otras opciones ... */}
                          </Select>
                        </div>
                      </Col>

                      {/* Barra de búsqueda */}
                      <Col xs={24} sm={12} md={7} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Búsqueda
                          </Title>
                          <Input
                            placeholder="Buscar..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                          />
                        </div>
                      </Col>

                      {/* Botón Limpiar */}
                      <Col xs={12} sm={6} md={3} lg={1}>
                        <Button
                          type="primary"
                          onClick={handleClearFilters}
                          icon={<ClearOutlined />}
                          style={{ width: "100%", marginTop: "24px" }}
                          disabled={
                            !(
                              selectedDepartamento ||
                              searchText ||
                              selectedMunicipio ||
                              selectedTipoLineaProductiva
                            )
                          }
                        >
                          {" "}
                        </Button>
                      </Col>
                      {/* Botón Exportar data */}
                      {permissionPrint && (
                        <Col
                          xs={12}
                          sm={6}
                          md={3}
                          lg={2}
                          style={{
                            width: "100%",
                            marginTop: "24px",
                            borderColor: "#52c41a",
                          }}
                        >
                          <ExportVisitasTecnicas
                            dataCompleta={filteredData}
                          ></ExportVisitasTecnicas>
                        </Col>
                      )}

                      {/* Botón Crear visita tecnica */}
                      <Col xs={12} sm={6} md={3} lg={2}>
                        {permissionInsert && (
                          <Button
                            type="primary"
                            style={{
                              width: "100%",
                              marginTop: "24px",
                              backgroundColor: "#52c41a",
                              borderColor: "#52c41a",
                            }}
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setInitialData({}); // Restablece initialData para crear una nueva novedad
                              setShowFormModal(true); // Abre el modal
                            }}
                          >
                            Nuevo
                          </Button>
                        )}
                      </Col>

                      {/* Total */}
                      <Col xs={12} sm={6} md={3} lg={3}>
                        <Card
                          size="small"
                          style={{
                            marginTop: "24px",
                            textAlign: "center",
                            backgroundColor: "#f0f5ff", // Fondo azul claro
                          }}
                        >
                          <Statistic
                            title="Total Visitas"
                            value={filteredData.length}
                            valueStyle={{
                              color: "#1890ff",
                              fontWeight: "bold",
                            }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <div className="row mb-4">
                <div className="col-12">
                  {filteredData.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={filteredData}
                      rowKey="id"
                      bordered
                      scroll={{ y: 500, x: "max-content" }}
                      pagination={{
                        current: currentPage, // página actual
                        pageSize: pageSize, // filas por página
                        total: filteredData.length, // total de registros
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} de ${total} registros`,
                        onChange: (page, size) => {
                          setCurrentPage(page);
                          setPageSize(size);
                        },
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", margin: "20px" }}>
                      <Alert
                        message="No hay resultados"
                        description="No se encontraron visitas técnicas que coincidan con tu búsqueda."
                        type="info"
                        showIcon
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal para el formulario de novedades */}
      {showFormModal && (
        <Modal
          open={showFormModal}
          onCancel={() => {
            handleCancel();
          }} // Cierra el modal
          footer={null} // Sin pie de modal
          width={1200} // Ajusta el ancho del modal aquí
        >
          <FormularioVisitaTecnica
            initialData={initialData}
            onClose={() => setShowFormModal(false)} // Pasar la función para cerrar el modal
            fetchData={fetchVisitasTecnicas}
          />
        </Modal>
      )}
    </>
  );
};

export default TablaVisitaTecnica;
